import { gsap } from 'gsap'
import { PerspectiveCamera } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { AUTHORING_CAMERA_MODE, type AppConfig } from '../config/appConfig'

export interface CameraPoseVector {
  x: number
  y: number
  z: number
}

export interface CameraPose {
  position: CameraPoseVector
  target: CameraPoseVector
}

export interface CameraSequenceStep extends CameraPose {
  duration: number
  ease: string
}

export interface CameraControlLimits {
  minDistance: number
  maxDistance: number
  minPolarAngle: number
  maxPolarAngle: number
  minAzimuthAngle?: number
  maxAzimuthAngle?: number
  rotateSpeed?: number
  enablePan?: boolean
  enableZoom?: boolean
}

const poseVector = ({ x, y, z }: CameraPoseVector): CameraPoseVector => ({
  x: Number(x.toFixed(6)),
  y: Number(y.toFixed(6)),
  z: Number(z.toFixed(6)),
})

export class CameraRig {
  public readonly camera: PerspectiveCamera
  public readonly controls: OrbitControls
  private readonly startPose: CameraPose
  private readonly intro: AppConfig['camera']['intro']
  private cameraTimeline: gsap.core.Timeline | null = null
  private cameraResolve: (() => void) | null = null
  private controlsEnabledBeforeAnimation = true

  public constructor(canvas: HTMLCanvasElement, config: AppConfig) {
    const cameraConfig = config.camera
    this.startPose = {
      position: { ...cameraConfig.position },
      target: { ...cameraConfig.target },
    }
    this.intro = cameraConfig.intro
    this.camera = new PerspectiveCamera(
      cameraConfig.fov,
      1,
      cameraConfig.near,
      cameraConfig.far,
    )
    this.camera.position.set(
      cameraConfig.position.x,
      cameraConfig.position.y,
      cameraConfig.position.z,
    )

    this.controls = new OrbitControls(this.camera, canvas)
    this.controls.target.set(
      cameraConfig.target.x,
      cameraConfig.target.y,
      cameraConfig.target.z,
    )
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.065
    this.controls.rotateSpeed = 0.45
    this.controls.zoomSpeed = 0.7
    this.controls.panSpeed = 0.55
    this.controls.screenSpacePanning = false
    this.controls.zoomToCursor = true
    const initialControls = AUTHORING_CAMERA_MODE
      ? config.authoringControls
      : config.controls
    this.controls.minDistance = initialControls.minDistance
    this.controls.maxDistance = initialControls.maxDistance
    this.controls.minPolarAngle = initialControls.minPolarAngle
    this.controls.maxPolarAngle = initialControls.maxPolarAngle
    this.controls.minAzimuthAngle = initialControls.minAzimuthAngle ?? -Infinity
    this.controls.maxAzimuthAngle = initialControls.maxAzimuthAngle ?? Infinity
    this.controls.rotateSpeed = initialControls.rotateSpeed ?? this.controls.rotateSpeed
    this.controls.enablePan = initialControls.enablePan ?? this.controls.enablePan
    this.controls.enableZoom = initialControls.enableZoom ?? this.controls.enableZoom
    this.controls.update()
    this.controls.saveState()
  }

  private applyPose(pose: CameraPose): void {
    this.camera.position.set(pose.position.x, pose.position.y, pose.position.z)
    this.controls.target.set(pose.target.x, pose.target.y, pose.target.z)
    this.controls.update()
  }

  private cancelAnimation(): void {
    if (!this.cameraTimeline) return
    const resolve = this.cameraResolve
    this.cameraTimeline.kill()
    this.cameraTimeline = null
    this.cameraResolve = null
    this.controls.enabled = this.controlsEnabledBeforeAnimation
    resolve?.()
  }

  private readonly completeAnimation = (): void => {
    this.cameraTimeline = null
    this.cameraResolve = null
    this.controls.enabled = this.controlsEnabledBeforeAnimation
    this.controls.update()
    this.controls.saveState()
  }

  public playIntro(): Promise<void> {
    this.cancelAnimation()
    this.applyPose(this.startPose)

    this.controlsEnabledBeforeAnimation = this.controls.enabled
    this.controls.enabled = false

    return new Promise((resolve) => {
      this.cameraResolve = resolve
      this.cameraTimeline = gsap.timeline({
        delay: this.intro.delay,
        onUpdate: () => this.controls.update(),
        onComplete: () => {
          this.completeAnimation()
          resolve()
        },
      })
      this.cameraTimeline.to(
        this.camera.position,
        {
          ...this.intro.position,
          duration: this.intro.duration,
          ease: this.intro.ease,
        },
        0,
      )
      this.cameraTimeline.to(
        this.controls.target,
        {
          ...this.intro.target,
          duration: this.intro.duration,
          ease: this.intro.ease,
        },
        0,
      )
    })
  }

  public playSequence(steps: readonly CameraSequenceStep[]): Promise<void> {
    this.cancelAnimation()
    if (steps.length === 0) return Promise.resolve()

    this.controlsEnabledBeforeAnimation = this.controls.enabled
    this.controls.enabled = false

    return new Promise((resolve) => {
      this.cameraResolve = resolve
      this.cameraTimeline = gsap.timeline({
        onUpdate: () => this.controls.update(),
        onComplete: () => {
          this.completeAnimation()
          resolve()
        },
      })

      let startTime = 0
      for (const step of steps) {
        this.cameraTimeline.to(
          this.camera.position,
          {
            ...step.position,
            duration: step.duration,
            ease: step.ease,
          },
          startTime,
        )
        this.cameraTimeline.to(
          this.controls.target,
          {
            ...step.target,
            duration: step.duration,
            ease: step.ease,
          },
          startTime,
        )
        startTime += step.duration
      }
    })
  }

  public update(): boolean {
    return this.controls.update()
  }

  public reset(): void {
    this.cancelAnimation()
    this.controls.reset()
  }

  public setPose(pose: CameraPose, saveAsResetPose = true): void {
    this.cancelAnimation()
    this.applyPose(pose)
    if (saveAsResetPose) this.controls.saveState()
  }

  public setFov(fov: number): void {
    const nextFov = Math.min(90, Math.max(10, fov))
    if (Math.abs(this.camera.fov - nextFov) < 0.0001) return
    this.camera.fov = nextFov
    this.camera.updateProjectionMatrix()
  }

  public setControlLimits(limits: CameraControlLimits): void {
    this.controls.minDistance = limits.minDistance
    this.controls.maxDistance = limits.maxDistance
    this.controls.minPolarAngle = limits.minPolarAngle
    this.controls.maxPolarAngle = limits.maxPolarAngle
    this.controls.minAzimuthAngle = limits.minAzimuthAngle ?? -Infinity
    this.controls.maxAzimuthAngle = limits.maxAzimuthAngle ?? Infinity
    if (limits.rotateSpeed !== undefined) this.controls.rotateSpeed = limits.rotateSpeed
    if (limits.enablePan !== undefined) this.controls.enablePan = limits.enablePan
    if (limits.enableZoom !== undefined) this.controls.enableZoom = limits.enableZoom
    this.controls.update()
  }

  public getPose(): CameraPose {
    return {
      position: poseVector(this.camera.position),
      target: poseVector(this.controls.target),
    }
  }

  public dispose(): void {
    this.cancelAnimation()
    this.controls.dispose()
  }
}
